import React, { useState, useRef, useEffect } from "react";
import { isMobile, isTouchDevice } from "common/utils/browser";
import { BACKGROUND_TYPE, COLORS } from "components/jiffy/design-lab/shared/constants/index";
import ImageMagnifier from "components/jiffy/image-magnifier/image-magnifier";
import { useDtfStore } from "../store";
import { SINGLE_IMAGE_ZOOM } from "./constants";
import "./SingleImageZoom.scss";
import { cn } from "tailwind-variants";

function SingleImageZoom({
  src,
  alt = "Preview Image",
  wrapperClass,
  isModal = false,
  position = "left",
  isArtwork = false,
  isFullWidth = false,
  isInline = false,
  imageBackground = "transparent",
  overrideBackgroundType = undefined,
  onLoad,
  onCanvasLoaded,
  ...restProps
}) {
  const [imageHeight, setImageHeight] = useState("unset");
  const [magnifier, setMagnifier] = useState(null);
  const [imageIntrinsicHeight, setImageIntrinsicHeight] = useState(0);
  const [imageIntrinsicWidth, setImageIntrinsicWidth] = useState(0);

  const store = useDtfStore();
  const imageContainerRef = useRef(null);
  const imageElementRef = useRef(null);

  function experiments() {
    return store.experiments;
  }

  function isSticker() {
    return store.isSticker;
  }

  function isStickerSample() {
    return store.isStickerSample;
  }

  function isStandardArtwork() {
    return store.isStandardArtwork;
  }

  function backgroundType() {
    return store.backgroundType;
  }

  function effectiveBackgroundType() {
    return overrideBackgroundType !== undefined ? overrideBackgroundType : backgroundType();
  }

  function isTappable() {
    return isTouchDevice() || isMobile();
  }

  function isTransparent() {
    return imageBackground === "transparent";
  }

  function computedImageBackground() {
    if (effectiveBackgroundType() === BACKGROUND_TYPE.GREEN) {
      return COLORS.GREEN_BACKGROUND;
    }
    return imageBackground;
  }

  function transparentBackgroundClasses() {
    return cn(
      isTransparent() &&
        effectiveBackgroundType() === BACKGROUND_TYPE.CHECKERED &&
        "single-image-zoom__image-wrapper--transparent-background",
      isStandardArtwork() &&
        effectiveBackgroundType() === BACKGROUND_TYPE.CHECKERED &&
        "single-image-zoom__image-wrapper--standard-artwork"
    );
  }

  function imageMaxHeight() {
    if (isSticker() || isStickerSample()) {
      return SINGLE_IMAGE_ZOOM.STICKER_MAX_HEIGHT;
    }
    return isArtwork ? "unset" : SINGLE_IMAGE_ZOOM.DEFAULT_IMAGE_HEIGHT;
  }

  function constants() {
    return SINGLE_IMAGE_ZOOM;
  }

  function setPreviewCanvasSize(...args) {
    return store.setPreviewCanvasSize(...args);
  }

  function initializeImage($image) {
    if ($image) {
      setImageIntrinsicHeight($image.naturalHeight);
      setImageIntrinsicWidth($image.naturalWidth);
    }
    if (!imageContainerRef.current) {
      return;
    }
    const { width, height } = imageContainerRef.current.getBoundingClientRect();
    setPreviewCanvasSize({
      width,
      height
    });
    if (!isTappable() && !magnifier) {
      const newMagnifier = new ImageMagnifier(wrapperClass, {
        isModal,
        position,
        useFixedPositioning: true
      });
      newMagnifier.initializeMagnifier();
      setMagnifier(newMagnifier);
    }
    if (onCanvasLoaded) {
      onCanvasLoaded();
    }
  }

  function handleImageLoad(event) {
    const $image = event.target;
    if (!isArtwork && $image && $image.naturalHeight < SINGLE_IMAGE_ZOOM.NATURAL_HEIGHT_THRESHOLD) {
      setImageHeight(SINGLE_IMAGE_ZOOM.DEFAULT_IMAGE_HEIGHT);
    }
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        initializeImage($image);
      });
    });
    if (onLoad) {
      onLoad(event);
    }
  }

  useEffect(
    () => () => {
      if (magnifier) {
        setMagnifier(null);
      }
    },
    [magnifier]
  );

  const transparentBgClasses = transparentBackgroundClasses();

  return (
    <div
      {...restProps}
      className={cn(
        "scope-83924df3",
        "single-image-zoom",
        "image-magnifier",
        wrapperClass,
        restProps.className
      )}
      ref={imageContainerRef}
      style={{
        "--83924df3-computedImageBackground": computedImageBackground(),
        "--83924df3-imageMaxHeight": imageMaxHeight(),
        "--83924df3-constants-INLINE_MAX_HEIGHT": constants().INLINE_MAX_HEIGHT,
        ...restProps.style
      }}
    >
      <div className="single-image-zoom__image-container scope-83924df3">
        <div
          className={cn("single-image-zoom__image-wrapper scope-83924df3", transparentBgClasses)}
        >
          <img
            className={cn("single-image-zoom__image js-magnify-image scope-83924df3", {
              "single-image-zoom__image--inline": isInline,
              "single-image-zoom__image--artwork": isArtwork,
              "single-image-zoom__image--full-width": isFullWidth
            })}
            ref={imageElementRef}
            src={src}
            alt={alt}
            onLoad={handleImageLoad}
          />
        </div>
      </div>
      {}
      <div
        className="image-magnifier__preview js-magnify-preview scope-83924df3"
        data-background-color={computedImageBackground()}
      />

      <div className="image-magnifier__lens js-magnify-lens scope-83924df3" />
    </div>
  );
}

export default SingleImageZoom;
